/**
 * @fileoverview Present explicit folder creation, naming, location, clipboard, and trash commands. Parent callbacks own canonical writes and access decisions; this component owns contextual interaction. `FileContextMenu.js` serves document actions and `FolderDestinationModal.js` owns destination selection. A selected menu item remains subject to current backend state.
 */

import React, { useEffect, useRef } from 'react';
import AppIcon from '../shared/AppIcon';
import './FileContextMenu.css';



export function canTrashFolder(folder, isAdmin) {
  if (!folder?.capabilities?.can_delete) return false;
  if (isAdmin) return true;
  // Missing counts are not evidence that a folder is empty.
  return folder.direct_document_count === 0
    && folder.descendant_document_count === 0
    && folder.child_folder_count === 0;
}

const MENU_WIDTH = 208;
const MENU_ITEM_HEIGHT = 42;
const VIEWPORT_GUTTER = 8;



export function folderContextMenuState(event, folder) {
  event.preventDefault();
  event.stopPropagation();
  const bounds = event.currentTarget?.getBoundingClientRect?.();
  const keyboardTriggered = event.type === 'keydown';
  const requestedX = keyboardTriggered || !event.clientX
    ? (bounds?.left || VIEWPORT_GUTTER) + 24
    : event.clientX;
  const requestedY = keyboardTriggered || !event.clientY
    ? (bounds?.top || VIEWPORT_GUTTER) + 28
    : event.clientY;
  const menuHeight = folder?.can_move_internally ? MENU_ITEM_HEIGHT * 3 + 12 : MENU_ITEM_HEIGHT + 12;
  return {
    folder,
    focusItem: keyboardTriggered,
    x: Math.max(VIEWPORT_GUTTER, Math.min(requestedX, window.innerWidth - MENU_WIDTH - VIEWPORT_GUTTER)),
    y: Math.max(VIEWPORT_GUTTER, Math.min(requestedY, window.innerHeight - menuHeight - VIEWPORT_GUTTER)),
  };
}



export function folderCreateContextMenuState(event, folder, canCreateFolder = true) {
  event.preventDefault();
  event.stopPropagation();
  const bounds = event.currentTarget?.getBoundingClientRect?.();
  const keyboardTriggered = event.type === 'keydown';
  const requestedX = keyboardTriggered || !event.clientX
    ? (bounds?.left || VIEWPORT_GUTTER) + 24
    : event.clientX;
  const requestedY = keyboardTriggered || !event.clientY
    ? (bounds?.top || VIEWPORT_GUTTER) + 28
    : event.clientY;
  const menuHeight = MENU_ITEM_HEIGHT * (canCreateFolder ? 2 : 1) + 12;
  return {
    folder,
    canCreateFolder,
    focusItem: keyboardTriggered,
    x: Math.max(VIEWPORT_GUTTER, Math.min(requestedX, window.innerWidth - MENU_WIDTH - VIEWPORT_GUTTER)),
    y: Math.max(VIEWPORT_GUTTER, Math.min(requestedY, window.innerHeight - menuHeight - VIEWPORT_GUTTER)),
  };
}



export function FolderCreateContextMenu({ menu, onClose, onNewFolder, onNewDocument, onPaste, canPaste = false }) {
  const firstItemRef = useRef(null);

  useEffect(

  () => {
    if (!menu) return undefined;
    const close =

    () => onClose();
    const handleKeyDown =

    (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return   () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu, onClose]);

  useEffect(() => {
    if (menu?.focusItem) firstItemRef.current?.focus();
  }, [menu]);

  if (!menu?.folder) return null;
  const label = menu.folder.name || menu.folder.folder_path || 'folder';
  return (
    <div
      className="documents-file-context-menu"
      role="menu"
      aria-label={`Create in ${label}`}
      style={{ left: menu.x, top: menu.y }}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => event.stopPropagation()}>
      {menu.canCreateFolder && (
        <button
          ref={firstItemRef}
          type="button"
          role="menuitem"
          onClick={() => {
            onClose();
            onNewFolder(menu.folder);
          }}>
          <AppIcon icon="pi pi-folder-plus" aria-hidden="true" />
          New subfolder
        </button>
      )}
      <button
        ref={menu.canCreateFolder ? undefined : firstItemRef}
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onNewDocument(menu.folder);
        }}>
        <AppIcon icon="pi pi-file-plus" aria-hidden="true" />
        New document
      </button>
      {onPaste && (
        <button
          type="button"
          role="menuitem"
          disabled={!canPaste}
          onClick={() => {
            onClose();
            onPaste(menu.folder);
          }}>
          <AppIcon icon="pi pi-clipboard" aria-hidden="true" />
          Paste here
        </button>
      )}
    </div>
  );
}



function FolderContextMenu({
  menu,
  onClose,
  onOpenInNewTab,
  onNewSubfolder,
  canCreateSubfolder = () => true,
  onMove,
  onTrash,
  onMergeWithMatter,
  canMergeWithMatter = () => false,
  onCopy,
  onCopyToClipboard,
  onCut,
  onPaste,
  canPaste = false,
  canTrash = () => false,
}) {
  const firstItemRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    const element = menuRef.current;
    if (!menu || !element) return;
    element.style.top = `${Math.max(VIEWPORT_GUTTER, Math.min(menu.y, window.innerHeight - element.offsetHeight - VIEWPORT_GUTTER))}px`;
  }, [menu]);

  useEffect(

  () => {
    if (!menu) return undefined;
    const close =

    () => onClose();
    const handleKeyDown =

    (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return   () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu, onClose]);

  useEffect(() => {
    if (menu?.focusItem) firstItemRef.current?.focus();
  }, [menu]);

  if (!menu?.folder) return null;
  const label = menu.folder.name || menu.folder.folder_path || 'Folder';
  const showNewSubfolder = Boolean(onNewSubfolder) && canCreateSubfolder(menu.folder);
  return (
    <div
      ref={menuRef}
      className="documents-file-context-menu"
      role="menu"
      aria-label={`${label} actions`}
      style={{ left: menu.x, top: menu.y }}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => event.stopPropagation()}>
      {showNewSubfolder && <button
        ref={firstItemRef}
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onNewSubfolder(menu.folder);
        }}>
        <AppIcon icon="pi pi-folder-plus" aria-hidden="true" />
        New subfolder
      </button>}
      {onOpenInNewTab && <button
        ref={showNewSubfolder ? undefined : firstItemRef}
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onOpenInNewTab(menu.folder);
        }}>
        <AppIcon icon="pi pi-plus" aria-hidden="true" />
        Open in new tab
      </button>}
      {menu.folder.can_move_internally && (
        <button
          ref={showNewSubfolder || onOpenInNewTab ? undefined : firstItemRef}
          type="button"
          role="menuitem"
          onClick={() => {
            onClose();
            onMove(menu.folder);
          }}>
          <AppIcon icon="pi pi-folder" aria-hidden="true" />
          Move folder
        </button>
      )}
      {menu.folder.can_move_internally && onMergeWithMatter && canMergeWithMatter(menu.folder) && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onClose();
            onMergeWithMatter(menu.folder);
          }}>
          <AppIcon icon="pi pi-briefcase" aria-hidden="true" />
          Merge with matter folder
        </button>
      )}
      {menu.folder.capabilities?.can_copy && onCopy && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCopy(menu.folder); }}>
          <AppIcon icon="pi pi-copy" aria-hidden="true" />
          Copy folder
        </button>
      )}
      {(menu.folder.can_move_internally || menu.folder.capabilities?.can_copy !== false) && onCopyToClipboard && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCopyToClipboard(menu.folder); }}>
          <AppIcon icon="pi pi-copy" aria-hidden="true" />
          Copy
        </button>
      )}
      {menu.folder.can_move_internally && onCut && (
        <button type="button" role="menuitem" onClick={() => { onClose(); onCut(menu.folder); }}>
          <AppIcon icon="pi pi-scissors" aria-hidden="true" />
          Cut
        </button>
      )}
      {onPaste && (
        <button type="button" role="menuitem" disabled={!canPaste} onClick={() => { onClose(); onPaste(menu.folder); }}>
          <AppIcon icon="pi pi-clipboard" aria-hidden="true" />
          Paste into folder
        </button>
      )}
      {canTrash(menu.folder) && onTrash && (
        <button type="button" role="menuitem" className="is-danger" onClick={() => { onClose(); onTrash(menu.folder); }}>
          <AppIcon icon="pi pi-trash" aria-hidden="true" />
          Move to trash
        </button>
      )}
    </div>
  );
}

export default FolderContextMenu;
